import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Ruler, User, Calendar, FileText, 
  ArrowLeft, Clock, CheckCircle, XCircle, Edit3
} from 'lucide-react'
import { Button } from '../components'
import measurementService from '../services/measurementService'
import { useAuth } from '../contexts/AuthContextTest'
import toast from 'react-hot-toast'

function MeasurementPreview() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  console.log('MeasurementPreview component rendered with ID:', id)
  const [measurement, setMeasurement] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch measurement data
  const fetchMeasurement = async () => {
    try {
      setIsLoading(true)
      console.log('Fetching measurement with ID:', id)
      const response = await measurementService.getById(parseInt(id))
      console.log('Full API response:', response)
      console.log('Response data:', response.data)
      
      // Handle different possible response structures
      const measurementData = response.data.measurement || response.data || response
      console.log('Measurement data:', measurementData)
      
      setMeasurement(measurementData)
    } catch (error) {
      console.error('Error fetching measurement:', error)
      console.error('Error details:', error.response?.data)
      toast.error('Failed to fetch measurement details')
      // Don't redirect on error, just show error state
    } finally {
      setIsLoading(false)
    }
  }


  // Fetch measurement data on component mount
  useEffect(() => {
    console.log('MeasurementPreview mounted with ID:', id)
    if (id && !isNaN(parseInt(id))) {
      fetchMeasurement()
    } else {
      console.error('Invalid measurement ID:', id)
      toast.error('Invalid measurement ID')
    }
  }, [id])


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatMeasurement = (value) => {
    return value ? `${value}"` : 'Not specified'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-base text-secondary-600">Loading measurement details...</p>
        </div>
      </div>
    )
  }

  if (!measurement && !isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <Ruler className="mx-auto h-12 w-12 text-secondary-400" />
          <h3 className="mt-2 text-base font-medium text-secondary-900">Measurement not found</h3>
          <p className="mt-1 text-base text-secondary-500">The measurement you're looking for doesn't exist.</p>
          <div className="mt-6">
            <Button onClick={() => navigate('/measurements')} variant="primary">
              Back to Measurements
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button
                onClick={() => navigate('/measurements')}
                variant="outline"
                className="mr-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-4xl font-bold text-secondary-900">
                  Measurement Details
                </h1>
                <p className="text-base text-secondary-600">
                  {measurement.customer?.firstName} {measurement.customer?.lastName} - {measurement.measurementType}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button 
                onClick={() => navigate('/measurements')} 
                variant="outline"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Edit Measurement
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-xl font-bold text-secondary-900 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Customer Information
                </h3>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-lg font-medium">
                    {measurement.customer?.firstName?.[0]}{measurement.customer?.lastName?.[0]}
                  </div>
                  <div className="ml-4">
                    <h4 className="text-xl font-bold text-secondary-900">
                      {measurement.customer?.firstName} {measurement.customer?.lastName}
                    </h4>
                    <p className="text-base text-secondary-600">
                      {measurement.customer?.phone} • {measurement.customer?.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Measurement Details */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-xl font-bold text-secondary-900 flex items-center">
                  <Ruler className="h-5 w-5 mr-2" />
                  Measurement Details
                </h3>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-base font-medium text-secondary-700 mb-2">Measurement Type</h4>
                    <p className="text-xl font-semibold text-secondary-900">{measurement.measurementType || 'Standard'}</p>
                  </div>
                  <div>
                    <h4 className="text-base font-medium text-secondary-700 mb-2">Status</h4>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      measurement.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {measurement.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {measurement.notes && (
                    <div className="md:col-span-2">
                      <h4 className="text-base font-medium text-secondary-700 mb-2">Notes</h4>
                      <p className="text-base text-secondary-900">{measurement.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Body Measurements */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-xl font-bold text-secondary-900">Body Measurements</h3>
                <p className="text-base text-secondary-600">All measurements in inches</p>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.length)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Length</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.bust)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Bust</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.waist)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Waist</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.hip)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Hip</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.shoulder)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Shoulder</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.armHole)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Arm Hole</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.sleeveLength)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Sleeve Length</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.chest)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Chest</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lower Body Measurements */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-xl font-bold text-secondary-900">Lower Body Measurements</h3>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.pentLength)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Pent Length</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.thigh)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Thigh</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.knee)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Knee</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.bottom)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Bottom</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Measurements */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-xl font-bold text-secondary-900">Additional Measurements</h3>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.neck)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Neck</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.backWidth)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Back Width</div>
                  </div>
                  <div className="text-center p-4 bg-secondary-50 rounded-lg">
                    <div className="text-4xl font-bold text-primary-600">{formatMeasurement(measurement.frontWidth)}</div>
                    <div className="text-base font-medium text-secondary-700 mt-2">Front Width</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-xl font-bold text-secondary-900">Quick Stats</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-base text-secondary-600">Status</span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    measurement.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {measurement.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-base text-secondary-600">Type</span>
                  <span className="text-base font-medium text-secondary-900">
                    {measurement.measurementType || 'Standard'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-base text-secondary-600">Created</span>
                  <span className="text-base font-medium text-secondary-900">
                    {formatDate(measurement.createdAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-base text-secondary-600">Updated</span>
                  <span className="text-base font-medium text-secondary-900">
                    {formatDate(measurement.updatedAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-secondary-200">
                <h3 className="text-xl font-bold text-secondary-900">Actions</h3>
              </div>
              <div className="px-6 py-4 space-y-3">
                <Button
                  onClick={() => navigate('/measurements')}
                  variant="outline"
                  className="w-full"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Measurements
                </Button>
                <Button
                  onClick={() => navigate('/measurements')}
                  variant="primary"
                  className="w-full"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Edit Measurement
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MeasurementPreview
